/**
 * cockpitReceivables — pure Aggregatoren für „Forderungs-Gesundheit"
 * (M-CFO-4, `architecture-cockpit-cfo.md` §8.4).
 *
 * Zwei Kennzahlen fürs Owner-Cockpit, beide ausschließlich aus den bereits
 * gebubbelten {@link CockpitOpenItemRow}-Rohdaten abgeleitet (0 neue
 * Firestore-Reads):
 *  - {@link computeDso}: durchschnittliche Zahlungsdauer (Days Sales
 *    Outstanding) mit **fail-safe**: bei zu wenigen erfassten Zahlungen wird
 *    KEINE irreführende Zahl produziert, sondern `insufficient` zurückgegeben.
 *  - {@link rankOverdueFocus}: überfällige Posten priorisiert nach
 *    **€ × Tage** (größter Cash-Hebel zuerst — NICHT chronologisch), Top-N.
 *
 * Reines TS-Modul ohne Firestore-/React-Import (Golden Rule „pure functions" —
 * trivial testbar). Geld durchgängig Integer-Cents, Datumsvergleiche über
 * UTC-Epochen (kein DST-Drift), malformed Daten werden ignoriert statt zu
 * NaN/0-Poisoning zu führen.
 *
 * @module utils/cockpitReceivables
 */

import type { CockpitOpenItemRow } from '../loaders/cockpitLoaders';

// ─── Datums-Helfer (bewusst dupliziert — Muster `cockpitChartData.ts`, kein ──
//     Cross-Import winziger interner Helfer) ────────────────────────────────

/**
 * Validates an ISO date string strictly enough to avoid NaN/garbage
 * comparisons downstream. A malformed value is treated as "unknown" — the
 * caller MUST ignore the entry (never coerce to `0`/`NaN`).
 */
function isValidISODate(value: string | null | undefined): value is string {
  if (typeof value !== 'string') return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return false;
  const month = Number(m[2]);
  const day = Number(m[3]);
  return month >= 1 && month <= 12 && day >= 1 && day <= 31;
}

/** UTC-midnight epoch (ms) for an ISO `YYYY-MM-DD` string — no DST drift. */
function utcEpochOf(dateISO: string): number {
  const y = Number(dateISO.slice(0, 4));
  const m = Number(dateISO.slice(5, 7));
  const d = Number(dateISO.slice(8, 10));
  return Date.UTC(y, m - 1, d);
}

/** Whole calendar days between two ISO dates (`to - from`), UTC-epoch-based. */
function daysBetweenUTC(fromISO: string, toISO: string): number {
  const MS_PER_DAY = 86_400_000;
  return Math.round((utcEpochOf(toISO) - utcEpochOf(fromISO)) / MS_PER_DAY);
}

// ─── DSO (Ø Zahlungsdauer) ───────────────────────────────────────────────────

/** Optionen für {@link computeDso} (Defaults als Konstanten testbar). */
export interface ComputeDsoOptions {
  /** Rückblick-Fenster in Tagen für die Basis-Menge (Default 365). */
  windowDays?: number;
  /** Mindest-Stichprobe für eine belastbare DSO (Default 5). Darunter ⇒ `insufficient`. */
  minSamples?: number;
  /** Mindest-Stichprobe je Trend-Teilfenster (Default 3). Darunter ⇒ kein Trend. */
  trendMinSamples?: number;
}

/**
 * Ergebnis von {@link computeDso}.
 * - `ok`: belastbare Kennzahl inkl. optionalem Trend und Ampel.
 * - `insufficient`: **fail-safe** — zu wenige erfasste Zahlungen für eine
 *   verlässliche Zahl; die Kachel zeigt „—" statt einer irreführenden DSO.
 */
export type DsoResult =
  | {
      kind: 'ok';
      /** Gerundeter Ø aus `paidDateISO − invoiceDateISO` über alle Fenster-Samples. */
      dsoDays: number;
      /** Zahl der einbezogenen bezahlten Rechnungen. */
      sampleCount: number;
      /**
       * Trend jüngstes Halbjahr vs. das davor; `null`, wenn eines der beiden
       * Teilfenster zu dünn ist (< `trendMinSamples`).
       */
      trend: { prevDsoDays: number; direction: 'up' | 'down' | 'flat' } | null;
      /** Ampel: `< 30` grün, `30–45` gelb, `> 45` rot (CFO-Leitplanke 5). */
      light: 'green' | 'yellow' | 'red';
    }
  | { kind: 'insufficient'; sampleCount: number };

/** Ein DSO-Sample: Zahlungs-Lag (Tage) + wie lange die Zahlung her ist. */
interface DsoSample {
  /** `paidDateISO − invoiceDateISO`, auf `>= 0` geklemmt (kein negativer DSO). */
  lagDays: number;
  /** `todayISO − paidDateISO` — Alter der Zahlung für die Trend-Fenster. */
  ageDays: number;
}

/** Ganzzahliger Durchschnitt einer nicht-leeren Zahlenliste (0 bei leer). */
function roundedMean(values: number[]): number {
  if (values.length === 0) return 0;
  const sum = values.reduce((acc, v) => acc + v, 0);
  return Math.round(sum / values.length);
}

/** Ampel-Zone aus dem DSO-Wert (Schwellen exakt: 30 ⇒ gelb, 45 ⇒ gelb, 46 ⇒ rot). */
function dsoLight(dsoDays: number): 'green' | 'yellow' | 'red' {
  if (dsoDays < 30) return 'green';
  if (dsoDays <= 45) return 'yellow';
  return 'red';
}

/** Halbjahres-Grenze (Tage) zwischen „jüngstes" und „vorheriges" Trend-Fenster. */
export const DSO_TREND_SPLIT_DAYS = 182;

/**
 * Berechnet die durchschnittliche Zahlungsdauer (DSO) aus bezahlten
 * offenen-Posten-Zeilen.
 *
 * Basis-Menge: Zeilen mit `status === 'paid'`, validem `paidDateISO` UND
 * validem `invoiceDateISO`, deren Zahlung innerhalb `windowDays` vor `todayISO`
 * liegt (Zahlung in der Zukunft = Datenmüll ⇒ verworfen). Je Zeile
 * `lagDays = paidDateISO − invoiceDateISO`, negativ auf `0` geklemmt (Zahlung
 * vor Rechnungsdatum ist Datenmüll, kein negativer DSO). DSO = gerundeter
 * Durchschnitt der `lagDays`.
 *
 * **Fail-safe:** `sampleCount < minSamples` ⇒ `insufficient` — NIE eine Zahl
 * aus 2–4 Rechnungen (irreführend).
 *
 * Trend: jüngstes Fenster A = bezahlt in den letzten {@link DSO_TREND_SPLIT_DAYS}
 * Tagen, Fenster B = `DSO_TREND_SPLIT_DAYS + 1 … windowDays` Tage davor. Nur
 * wenn BEIDE Fenster `>= trendMinSamples` Samples haben, entsteht ein Trend;
 * `direction`: `|dsoA − dsoB| <= 1` Tag ⇒ `flat`, sonst `up` (langsamer/schlechter)
 * bzw. `down` (schneller/besser). `prevDsoDays` = Ø des älteren Fensters B.
 *
 * @param rows - gebubbelte {@link CockpitOpenItemRow}[] (0 neue Reads).
 * @param todayISO - Referenzdatum `YYYY-MM-DD` (Parameter → pure/testbar).
 * @param opts - Schwellwerte (Defaults: `windowDays=365`, `minSamples=5`, `trendMinSamples=3`).
 * @returns {@link DsoResult}.
 */
export function computeDso(
  rows: CockpitOpenItemRow[],
  todayISO: string,
  opts?: ComputeDsoOptions,
): DsoResult {
  const windowDays = opts?.windowDays ?? 365;
  const minSamples = opts?.minSamples ?? 5;
  const trendMinSamples = opts?.trendMinSamples ?? 3;

  // Malformed Referenzdatum ⇒ keine belastbare Aussage (fail-safe).
  if (!isValidISODate(todayISO) || !Array.isArray(rows)) {
    return { kind: 'insufficient', sampleCount: 0 };
  }

  const samples: DsoSample[] = [];
  for (const row of rows) {
    if (!row || row.status !== 'paid') continue;
    if (!isValidISODate(row.paidDateISO) || !isValidISODate(row.invoiceDateISO)) continue;
    const ageDays = daysBetweenUTC(row.paidDateISO, todayISO);
    // Zahlung in der Zukunft oder außerhalb des Fensters ⇒ ignorieren.
    if (ageDays < 0 || ageDays > windowDays) continue;
    const lagDays = Math.max(0, daysBetweenUTC(row.invoiceDateISO, row.paidDateISO));
    samples.push({ lagDays, ageDays });
  }

  if (samples.length < minSamples) {
    return { kind: 'insufficient', sampleCount: samples.length };
  }

  const dsoDays = roundedMean(samples.map((s) => s.lagDays));

  const recent = samples.filter((s) => s.ageDays <= DSO_TREND_SPLIT_DAYS);
  const older = samples.filter((s) => s.ageDays > DSO_TREND_SPLIT_DAYS);
  let trend: { prevDsoDays: number; direction: 'up' | 'down' | 'flat' } | null = null;
  if (recent.length >= trendMinSamples && older.length >= trendMinSamples) {
    const dsoRecent = roundedMean(recent.map((s) => s.lagDays));
    const dsoOlder = roundedMean(older.map((s) => s.lagDays));
    const delta = dsoRecent - dsoOlder;
    const direction: 'up' | 'down' | 'flat' =
      Math.abs(delta) <= 1 ? 'flat' : delta > 0 ? 'up' : 'down';
    trend = { prevDsoDays: dsoOlder, direction };
  }

  return { kind: 'ok', dsoDays, sampleCount: samples.length, trend, light: dsoLight(dsoDays) };
}

// ─── Überfällig-Fokus (größter Cash-Hebel zuerst) ────────────────────────────

/** Eine priorisierte überfällige Zeile für die Fokusliste. */
export interface OverdueFocusRow {
  invoiceId: string;
  invoiceNumber: string | null;
  buyerName: string | null;
  openAmountCents: number;
  /** `todayISO − dueDateISO`, auf `>= 0` geklemmt. */
  daysOverdue: number;
  /** `openAmountCents × daysOverdue` — der Cash-Hebel (Sortier-Score). */
  scoreCentDays: number;
}

/**
 * Priorisiert die überfälligen offenen Posten nach ihrem Cash-Hebel
 * (`openAmountCents × daysOverdue`) — größter zuerst, NICHT chronologisch.
 *
 * Nur `status === 'overdue'`-Zeilen mit validem `dueDateISO` (ohne Fälligkeit
 * lässt sich nichts als überfällig priorisieren — solche Zeilen erscheinen im
 * Liquiditäts-Bucket `noDueDate`, nicht hier). `daysOverdue` wird auf `>= 0`
 * geklemmt (inkonsistente „overdue"-Zeile mit Fälligkeit in der Zukunft ⇒
 * Score 0, sortiert ans Ende). Sortierung: `scoreCentDays` desc, Tie-Break
 * `openAmountCents` desc, dann `invoiceId` asc (deterministisch für Tests).
 *
 * @param rows - gebubbelte {@link CockpitOpenItemRow}[] (0 neue Reads).
 * @param todayISO - Referenzdatum `YYYY-MM-DD`.
 * @param topN - maximale Zeilenzahl (Default 3).
 * @returns bis zu `topN` {@link OverdueFocusRow} in Prioritätsreihenfolge (leer, wenn nichts überfällig).
 */
export function rankOverdueFocus(
  rows: CockpitOpenItemRow[],
  todayISO: string,
  topN = 3,
): OverdueFocusRow[] {
  if (!isValidISODate(todayISO) || !Array.isArray(rows) || topN <= 0) return [];

  const focus: OverdueFocusRow[] = [];
  for (const row of rows) {
    if (!row || row.status !== 'overdue') continue;
    if (!isValidISODate(row.dueDateISO)) continue;
    const daysOverdue = Math.max(0, daysBetweenUTC(row.dueDateISO, todayISO));
    const openAmountCents = Number.isFinite(row.openAmountCents) ? row.openAmountCents : 0;
    focus.push({
      invoiceId: row.invoiceId,
      invoiceNumber: row.invoiceNumber,
      buyerName: row.buyerName,
      openAmountCents,
      daysOverdue,
      scoreCentDays: openAmountCents * daysOverdue,
    });
  }

  focus.sort((a, b) => {
    if (b.scoreCentDays !== a.scoreCentDays) return b.scoreCentDays - a.scoreCentDays;
    if (b.openAmountCents !== a.openAmountCents) return b.openAmountCents - a.openAmountCents;
    return a.invoiceId < b.invoiceId ? -1 : a.invoiceId > b.invoiceId ? 1 : 0;
  });

  return focus.slice(0, topN);
}
